/**
 * BCH utility functions
 */

/** Calculate tax breakdown from a subtotal and rate */
export function calcTax(subtotal: number, taxRate: number): { taxAmount: number; total: number } {
  const taxAmount = parseFloat((subtotal * (taxRate / 100)).toFixed(2));
  const total = parseFloat((subtotal + taxAmount).toFixed(2));
  return { taxAmount, total };
}

/** Calculate a discount amount from a subtotal */
export function calcDiscount(
  subtotal: number,
  type: 'percent' | 'flat',
  value: number
): number {
  if (value <= 0) return 0;
  const raw = type === 'percent'
    ? subtotal * (value / 100)
    : Math.min(value, subtotal); // flat can't exceed subtotal
  return parseFloat(Math.min(raw, subtotal).toFixed(2));
}

export interface TaxLineCalc {
  name: string;
  rate: number;
  amount: number;
}

interface CartItemLike {
  product: { price: number; taxRateId?: string };
  qty: number;
}

interface TaxRateLike {
  id: string;
  name: string;
  rate: number;
}

/**
 * Calculate multi-rate tax from a cart.
 * Each product may have its own taxRateId; falls back to the default rate.
 */
export function calcCartTax(
  cart: CartItemLike[],
  taxRates: TaxRateLike[],
  defaultRate: number,
  defaultName: string,
  discountedSubtotal: number,
  originalSubtotal: number,
): TaxLineCalc[] {
  if (cart.length === 0) {
    // Keypad mode — single default rate on discounted subtotal
    if (defaultRate <= 0) return [];
    const amount = parseFloat((discountedSubtotal * (defaultRate / 100)).toFixed(2));
    return amount > 0 ? [{ name: defaultName, rate: defaultRate, amount }] : [];
  }

  // Discount ratio — apply proportionally across items
  const discountRatio = originalSubtotal > 0 ? discountedSubtotal / originalSubtotal : 1;

  // Group items by effective tax rate id
  const groups = new Map<string, { name: string; rate: number; base: number }>();

  for (const item of cart) {
    const lineTotal = item.product.price * item.qty * discountRatio;
    let taxRate: TaxRate | undefined;
    if (item.product.taxRateId) {
      taxRate = taxRates.find((r) => r.id === item.product.taxRateId);
    }
    const name = taxRate ? taxRate.name : defaultName;
    const rate = taxRate ? taxRate.rate : defaultRate;
    const key = `${name}:${rate}`;
    const existing = groups.get(key);
    if (existing) existing.base += lineTotal;
    else groups.set(key, { name, rate, base: lineTotal });
  }

  const lines: TaxLineCalc[] = [];
  for (const { name, rate, base } of groups.values()) {
    if (rate <= 0) continue;
    const amount = parseFloat((base * (rate / 100)).toFixed(2));
    if (amount > 0) lines.push({ name, rate, amount });
  }
  return lines;
}

/** Format a BCH amount with 8 decimal places, trimming trailing zeros */
export function formatBCH(amount: number): string {
  if (amount === 0) return '0';
  // Show up to 8 decimal places, trim trailing zeros
  return amount.toFixed(8).replace(/\.?0+$/, '');
}

/** Format a fiat amount with currency symbol */
export function formatFiat(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/** Convert fiat to BCH using the current price */
export function fiatToBCH(fiatAmount: number, bchPriceInFiat: number): number {
  if (bchPriceInFiat <= 0) return 0;
  return fiatAmount / bchPriceInFiat;
}

/** Generate a bitcoincash: URI for QR code */
export function generateBCHUri(address: string, amount: number, label?: string): string {
  // Strip prefix if already present to avoid bitcoincash:bitcoincash:...
  const bare = address.replace(/^bitcoincash:/i, '');
  const params = new URLSearchParams();
  if (amount > 0) {
    params.set('amount', formatBCH(amount));
  }
  if (label) {
    params.set('label', label);
  }
  const paramStr = params.toString();
  const base = `bitcoincash:${bare}`;
  return paramStr ? `${base}?${paramStr}` : base;
}

/** Supported currencies */
export const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'AU$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'VES', symbol: 'Bs.', name: 'Venezuelan Bolívar' },
  { code: 'ARS', symbol: '$', name: 'Argentine Peso' },
  { code: 'MZN', symbol: 'MT', name: 'Mozambican Metical' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
];

/** Get currency symbol */
export function getCurrencySymbol(code: string): string {
  return CURRENCIES.find((c) => c.code === code)?.symbol ?? code;
}
